import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  AdminUserSortDirection,
  AdminUserStatus,
  defaultAdminUsersFilter,
  isAdminUserFilterRole,
  isAdminUserSortProperty,
  type AdminUserSortProperty,
  type AdminUsersFilter,
} from './model';

export interface UsersRouteSearch {
  search?: string;
  statuses?: string;
  role?: string;
  page?: number;
  size?: number;
  sortProperty?: AdminUserSortProperty | null;
  sortDirection?: AdminUserSortDirection;
}

export function usersRouteSearchSchema(search: Record<string, unknown>): UsersRouteSearch {
  const searchTerm = optionalTrimmedString(search['search']);
  const statuses = typeof search['statuses'] === 'string' ? search['statuses'] : undefined;
  const rawRole = optionalTrimmedString(search['role']);
  const role = rawRole && isAdminUserFilterRole(rawRole) ? rawRole : undefined;
  const page = optionalPositiveInteger(search['page']);
  const size = optionalPositiveInteger(search['size']);
  const rawSortProperty = optionalTrimmedString(search['sortProperty']);
  const sortProperty =
    rawSortProperty && isAdminUserSortProperty(rawSortProperty) ? rawSortProperty : undefined;
  const sortDirection = Object.values(AdminUserSortDirection).includes(
    search['sortDirection'] as AdminUserSortDirection,
  )
    ? (search['sortDirection'] as AdminUserSortDirection)
    : undefined;
  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(statuses !== undefined ? { statuses } : {}),
    ...(role ? { role } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(search['sortProperty'] === null ? { sortProperty: null } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  };
}

const adminUserStatuses: ReadonlySet<string> = new Set(Object.values(AdminUserStatus));

export function parseUsersRouteSearch(search: UsersRouteSearch): AdminUsersFilter {
  const statuses =
    search.statuses === undefined
      ? defaultAdminUsersFilter.statuses
      : search.statuses
          .split(',')
          .filter((status): status is AdminUserStatus => adminUserStatuses.has(status));
  return {
    searchTerm: search.search ?? defaultAdminUsersFilter.searchTerm,
    statuses,
    role: search.role ?? defaultAdminUsersFilter.role,
    page: search.page ? search.page - 1 : defaultAdminUsersFilter.page,
    size: search.size ?? defaultAdminUsersFilter.size,
    sortProperty:
      search.sortProperty === null
        ? null
        : (search.sortProperty ?? defaultAdminUsersFilter.sortProperty),
    sortDirection: search.sortDirection ?? defaultAdminUsersFilter.sortDirection,
  };
}

export function serializeUsersRouteSearch(filter: AdminUsersFilter): UsersRouteSearch {
  const statuses = filter.statuses.join(',');
  return {
    ...(filter.searchTerm.trim() ? { search: filter.searchTerm.trim() } : {}),
    ...(statuses !== defaultAdminUsersFilter.statuses.join(',') ? { statuses } : {}),
    ...(filter.role ? { role: filter.role } : {}),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaultAdminUsersFilter.size ? { size: filter.size } : {}),
    ...(filter.sortProperty !== defaultAdminUsersFilter.sortProperty
      ? { sortProperty: filter.sortProperty }
      : {}),
    ...(filter.sortDirection !== defaultAdminUsersFilter.sortDirection
      ? { sortDirection: filter.sortDirection }
      : {}),
  };
}
